/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useTaskSubscriber from '../../hooks/useTaskSubscriber'
import { requestOverviewData } from '../../store/stats/StatsActions'
import { StatsState } from '../../store/stats/StatsTypes'
import { useAppDispatch, useAppSelector } from '../../store/store'
import Card from '../Card/Card'
import './HomePage.css'
import API, { fetchRelaysData, formatParamsForApi } from 'src/api/api'
import BlockchainIcon from 'src/assets/components/BlockchainIcon'
import Tabs from '../shared/Tabs'
import BigNumber from 'bignumber.js'
import { BridgeId, getBridgeNameFromBridgeId } from 'src/constants/BlockchainConstants'
import ServerTable, { RequestDataParams } from 'src/components/Tables/ServerTable'
import { FilterState } from 'src/constants/DataModels'
import { DEFAULT_END_DATE_FORMAT, DEFAULT_START_DATE_FORMAT, MOBILE_RELAY_COLUMNS, RELAY_COLUMNS } from 'src/components/Relays/RelaysPage'
import ViewMoreButton from 'src/components/Tables/ViewMoreButton'

const HomePage: React.FC = () => {
  const dispatch = useAppDispatch()
  const selector = useAppSelector

  // network state between Mainnet or Testnet
  const network = selector((state) => state.NetworkReducer.network)
  // const supportedBlockchains = selector((state) => state.StatsReducer.supportedBlockchains)

  // state from stats/overview store
  const stats: StatsState = selector((state) => state.StatsReducer)
  const statsLoading: boolean = useTaskSubscriber('runStatsOverviewSaga')
  const overview = stats.overview
  const totalRelay = overview.relay_count
  const totalInTransit = overview.in_transit_count
  const dayTx = stats.dayTx

  // const [supportedBlockchains, setSupportedBlockchains] = useState<SupportedBlockchains | undefined>(undefined)
  const [blockchainStats, setBlockchainStats] = useState<SortableBridgeBlockchainStats>({})
  const [currentBridgeSummary, setCurrentBridgeSummary] = useState(BridgeId.IBC)

  type BlockchainStats = {
    relay_count: number
    transfers_usd_value: string
  }

  type BlockchainStatsRow = BlockchainStats & { blockchain: string }

  type BridgeBlockchainStats = {
    [bridge_id: number]: { [blockchain: string]: BlockchainStats }
  }

  type SortableBridgeBlockchainStats = {
    [bridge_id: string]: Array<BlockchainStatsRow>
  }

  // fetch data
  useEffect(() => {
    const fetchData = async () => {
      const stats = await API.get<BridgeBlockchainStats>(`/stats/bridges`)
      let sortableBlockchainStats: SortableBridgeBlockchainStats = {}
      Object.entries(stats.data).reverse().forEach(([bridge, blockchainStats]) => {
        Object.entries(blockchainStats).forEach(([blockchain, blockchainStat]) => {
          if (!sortableBlockchainStats[bridge]) {
            sortableBlockchainStats[bridge] = []
          }
          sortableBlockchainStats[bridge].push({ ...blockchainStat, blockchain })
        })
      })

      // sort bridge blockchains by usd volume
      Object.entries(sortableBlockchainStats).forEach(([_, blockchains]) => {
        blockchains.sort((a, b) => {
          return new BigNumber(b.transfers_usd_value).minus(new BigNumber(a.transfers_usd_value)).toNumber()
        })
      })

      setBlockchainStats(sortableBlockchainStats)

      // if can't find for whatever reason
      if (!stats.data[currentBridgeSummary]) {
        // set to a random one in data
        setCurrentBridgeSummary(Number(Object.keys(stats.data).reverse()[0]))
      }
    }
    fetchData()
  }, [network])

  // initial render to set states
  useEffect(() => {
    dispatch(requestOverviewData())
  }, [network])

  const handleDataRequest = useCallback(async (params: RequestDataParams) => {
    // add default date range if none exists
    const filterWithDefaults: FilterState = {
      ...params.filter,
      start_time: params.filter.start_time || DEFAULT_START_DATE_FORMAT,
      end_time: params.filter.end_time || DEFAULT_END_DATE_FORMAT,
    }

    const response = await fetchRelaysData(formatParamsForApi({
      ...params,
      filter: filterWithDefaults,
    }))
    return response.data
  }, [])

  return (
    <div className="container main-wrapper">
      <div className="dashboard-cards">
        <div className="dashboard-card">
          <h4 className="dashboard-card-title">{'Total Relays'}</h4>
          <h1 className="dashboard-card-description">{totalRelay.toString()}</h1>
        </div>
        <div className="dashboard-card">
          <h4 className="dashboard-card-title">{'24H Relays'}</h4>
          <h1 className="dashboard-card-description">{dayTx.toString()}</h1>
        </div>
        <div className="dashboard-card">
          <h4 className="dashboard-card-title">{'In Progress'}</h4>
          <h1 className="dashboard-card-description">{totalInTransit.toString()}</h1>
        </div>
      </div>
      <div className="bridge-activity-wrapper">
        <Card title="Bridge Activity">
          {blockchainStats && (
            <Tabs tabs={
              Object.entries(blockchainStats).reverse().map(([bridge_id, _]) => {
                const bridgeId = Number(bridge_id) as BridgeId
                let bridgeLabel = getBridgeNameFromBridgeId(bridgeId)
                return {
                  label: (
                    <div className={'bridge-title'} key={bridge_id}>
                      <span>{bridgeLabel}</span>
                      <small>{`(${blockchainStats[bridgeId]?.length} chains)`}</small>
                    </div>
                  ),
                  value: bridgeId.toString(),
                  selected: currentBridgeSummary === bridgeId,
                  onClick: () => setCurrentBridgeSummary(bridgeId),
                }
              })
            }
                  wrapperClass="bridge-activity"
            >
              <div className="supported-chains">
                {
                  blockchainStats && blockchainStats[currentBridgeSummary] && blockchainStats[currentBridgeSummary].map((stats, index) => {
                    if (stats.blockchain === 'unknown-ibc') return null
                    const fmt = {
                      groupSeparator: ',',
                      groupSize: 3,
                      prefix: '$'
                    }
                    const transfersUsdValue = new BigNumber(stats.transfers_usd_value).toFormat(0, fmt)
                    return (
                      <div className="supported-chain-wrapper" key={stats.blockchain}>
                        <div className="supported-chain-header" key={stats.blockchain}>
                          <div>{stats.blockchain}</div>
                          <div className="supported-chain-icon">
                            <BlockchainIcon blockchain={stats.blockchain} />
                          </div>
                        </div>
                        <div className="supported-chain-stats">
                          <div className="supported-chain-stat"
                          >Relays: {stats ? stats.relay_count : '-'}</div>
                          <div className="supported-chain-stat"
                          >Volume: {stats ? transfersUsdValue : '-'}</div>
                        </div>
                      </div>
                    )
                  })
                }
              </div>
            </Tabs>
          )}
        </Card>
      </div>
      <div className="latest-table">
        <ServerTable name="Latest Relays"
                     columns={RELAY_COLUMNS}
                     mobileColumns={MOBILE_RELAY_COLUMNS}
                     onRequestData={handleDataRequest}
                     filters={[]}
                     viewMorePath={'relays'}
                     showPagination={false}
                     showSort={false}
        />
      </div>
    </div>
  )
}

export default HomePage
